import type { ReactNode } from 'react';
import {
  cn,
  Flex,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Text,
} from '@kognitos/lattice';
import type { IEmailFilters } from '@/modules/triggers/components/EmailTriggerDialog/types';
import i18n from '@/shared/i18n';

const t = (key: string) => i18n.t(key, { ns: 'automation' });

/**
 * Muted, single-line subline under a trigger badge. Overflow is clipped with an
 * ellipsis so long values (mailbox addresses) keep the column width.
 *
 * @param props - Component props
 * @param props.children - The subline text
 * @returns Truncating subline text
 */
export const TriggerDetailText = ({ children }: { children: string }) => (
  <Text
    level="xSmall"
    color="muted"
    ellipsis={{ tooltip: true }}
    className="w-full"
    data-testid="trigger-subline"
  >
    {children}
  </Text>
);

/**
 * Wraps the trigger badge so hovering it reveals the trigger's full detail,
 * opening above the badge and aligned to its left edge.
 *
 * @param props - Component props
 * @param props.content - Detail content revealed on hover
 * @param props.wide - Sizes the card for the schedule preview instead of 320px
 * @param props.children - The badge that opens the card
 * @returns Hoverable badge
 */
export const TriggerDetailHoverCard = ({
  content,
  wide,
  children,
}: {
  content: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) => (
  <HoverCard openDelay={150} closeDelay={100}>
    <HoverCardTrigger asChild>
      {/* inline-flex so the wrapper adds no line-height above the badge,
          keeping the badge-to-subline gap identical to un-hoverable rows. */}
      <span className="inline-flex cursor-default">{children}</span>
    </HoverCardTrigger>
    <HoverCardContent
      align="start"
      side="top"
      className={cn('rounded-xl', wide ? 'w-auto max-w-[620px] p-0' : 'w-80')}
      onClick={e => e.stopPropagation()}
    >
      {content}
    </HoverCardContent>
  </HoverCard>
);

/**
 * Eyebrow heading for a section of trigger detail.
 *
 * @param props - Component props
 * @param props.children - Heading text
 * @returns Section heading
 */
const SectionTitle = ({ children }: { children: string }) => (
  <Text
    level="xSmall"
    color="muted"
    weight="medium"
    className="uppercase tracking-wider"
  >
    {children}
  </Text>
);

/**
 * A single "<label> / <value>" row of trigger detail. The label truncates and
 * the value clamps to two lines, so a long connection or event name can't grow
 * the card past its fixed width.
 *
 * @param props - Component props
 * @param props.label - Field label
 * @param props.value - Field value
 * @returns Labelled detail row
 */
const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Flex direction="column" gap={0.5}>
    <Text level="xSmall" color="muted" className="w-full truncate">
      {label}
    </Text>
    <Text level="small" className="line-clamp-2 w-full break-all">
      {value}
    </Text>
  </Flex>
);

const EMAIL_FILTER_LABELS: {
  key: keyof IEmailFilters;
  labelKey: string;
}[] = [
  { key: 'from', labelKey: 'automation.runs.triggerDetail.filter.from' },
  { key: 'cc', labelKey: 'automation.runs.triggerDetail.filter.cc' },
  {
    key: 'subjectHasWords',
    labelKey: 'automation.runs.triggerDetail.filter.subjectHasWords',
  },
  {
    key: 'subjectDoesntHaveWords',
    labelKey: 'automation.runs.triggerDetail.filter.subjectDoesntHaveWords',
  },
  {
    key: 'bodyHasWords',
    labelKey: 'automation.runs.triggerDetail.filter.bodyHasWords',
  },
  {
    key: 'bodyDoesntHaveWords',
    labelKey: 'automation.runs.triggerDetail.filter.bodyDoesntHaveWords',
  },
];

/**
 * Hover detail for an email trigger: the mailbox address and any filters the
 * trigger was configured with.
 *
 * @param props - Component props
 * @param props.address - Mailbox address that received the email
 * @param props.filters - Filters configured on the trigger
 * @returns Email trigger detail content
 */
export const EmailTriggerPopover = ({
  address,
  filters,
}: {
  address: string;
  filters?: IEmailFilters;
}) => {
  const activeFilters = EMAIL_FILTER_LABELS.filter(
    ({ key }) => filters?.[key],
  ).map(({ key, labelKey }) => ({
    label: t(labelKey),
    value: String(filters?.[key]),
  }));
  const hasAttachments = filters?.hasAttachments;

  const hasFilters = activeFilters.length > 0 || hasAttachments;

  return (
    <Flex direction="column" gap={4}>
      <Flex direction="column" gap={0.5}>
        <SectionTitle>
          {t('automation.runs.triggerDetail.emailAddress')}
        </SectionTitle>
        <Text level="small" className="break-all">
          {address}
        </Text>
      </Flex>

      <Flex direction="column" gap={2}>
        <SectionTitle>
          {t('automation.runs.triggerDetail.filters')}
        </SectionTitle>
        {activeFilters.map(({ label, value }) => (
          <DetailRow key={label} label={label} value={value} />
        ))}
        {hasAttachments && (
          <Text level="small">
            {t('automation.runs.triggerDetail.filter.hasAttachments')}
          </Text>
        )}
        {!hasFilters && (
          <Text level="small" color="muted">
            {t('automation.runs.triggerDetail.noFilters')}
          </Text>
        )}
      </Flex>
    </Flex>
  );
};

/**
 * Hover detail for a webhook or poll trigger: the integration, the connection
 * it runs through, and the event that fires it.
 *
 * @param props - Component props
 * @param props.app - Integration display name
 * @param props.connection - Connection name
 * @param props.event - Trigger event type
 * @returns Webhook trigger detail content
 */
export const WebhookTriggerPopover = ({
  app,
  appIcon,
  connection,
  event,
}: {
  app?: string;
  appIcon?: string;
  connection?: string;
  event?: string;
}) => (
  <Flex direction="column" gap={3}>
    {app && (
      <Flex direction="column" gap={0.5}>
        <Text level="xSmall" color="muted">
          {t('automation.runs.triggerDetail.integration')}
        </Text>
        <Flex align="center" gap={2}>
          {appIcon && (
            <img
              src={appIcon}
              alt={`${app} icon`}
              className="size-3 shrink-0 object-contain"
            />
          )}
          <Text level="small" className="line-clamp-2 min-w-0">
            {app}
          </Text>
        </Flex>
      </Flex>
    )}
    {connection && (
      <DetailRow
        label={t('automation.runs.triggerDetail.connection')}
        value={connection}
      />
    )}
    {event && (
      <DetailRow
        label={t('automation.runs.triggerDetail.event')}
        value={event}
      />
    )}
  </Flex>
);
